import { useMutation, useQueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { sqlImportRowKeys, type SqlImportRow } from './entity';

export interface CancelSqlImportRowInput {
  workspaceId: number;
  rowId: string;
}

/**
 * Cancel a run that is still processing. The backend answers 400 for a run that
 * has already finished, and returns the row attributed to its parent import.
 *
 * @public
 */
export async function cancelSqlImportRow({ workspaceId, rowId }: CancelSqlImportRowInput): Promise<SqlImportRow> {
  const data = await hydraUiApi.sqlImportRows.canceled({
    params: { path: { environment_id: String(workspaceId), id: rowId } },
  });
  return data.sql_import_row;
}

type UseCancelSqlImportRowVariables = Omit<CancelSqlImportRowInput, 'workspaceId'>;

interface UseCancelSqlImportRowOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<SqlImportRow, UseCancelSqlImportRowVariables>;
}

/** @public */
export const useCancelSqlImportRow = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseCancelSqlImportRowOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseCancelSqlImportRowVariables) => cancelSqlImportRow({ workspaceId, ...variables }),
    onSuccess: (canceled, ...callbackArgs) => {
      // Re-read the import's history rather than patching one row: canceling a
      // relationship child run can change the parent run's status too.
      void qc.invalidateQueries({ queryKey: sqlImportRowKeys.list(workspaceId, canceled.import_id) });
      onSuccess?.(canceled, ...callbackArgs);
    },
  });
};
